import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  GetDraftQuotationResponseQueryResponse,
  QuotationOutboxDto,
  SaveQuotationResponseCommandRequest,
  SaveQuotationResponseCommandResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export class QuotationOutboxClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/quotations/outbox`, onRequestChange)
  }

  list(): Promise<QuotationOutboxDto[]> {
    return this.GetAsync({ relativePath: '', id: 'ListQuotationOutbox' })
  }

  detail(quotationId: string): Promise<QuotationOutboxDto> {
    return this.GetAsync({
      relativePath: `/${quotationId}`,
      id: `GetQuotationOutbox-${quotationId}`,
    })
  }

  draft(projectId: string): Promise<GetDraftQuotationResponseQueryResponse> {
    return this.GetAsync({
      relativePath: '/draft',
      params: { projectId },
      id: `GetQuotationOutboxDraft-${projectId}`,
    })
  }

  save(
    quotationId: string,
    request: SaveQuotationResponseCommandRequest,
  ): Promise<SaveQuotationResponseCommandResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/save`,
      data: request,
      id: `SaveQuotationOutbox-${quotationId}`,
    })
  }

  send(quotationId: string, projectId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/send`,
      params: { projectId },
      id: `SendQuotationOutbox-${quotationId}`,
    })
  }

  retry(quotationId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/retry`,
      id: `RetryQuotationOutbox-${quotationId}`,
    })
  }

  changeChannel(
    quotationId: string,
    request: ChangeChannelRequest,
  ): Promise<QuotationOutboxDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${quotationId}/channel`,
      data: request,
      id: `ChangeQuotationOutboxChannel-${quotationId}`,
    })
  }

  download(quotationId: string) {
    return this.DownloadAsync({
      relativePath: `/${quotationId}/document`,
      id: `DownloadQuotationOutbox-${quotationId}`,
    })
  }

  uploadDecision(file: File, quotationId?: string): Promise<unknown> {
    const data = new FormData()
    data.append('file', file)
    if (quotationId) data.append('quotationId', quotationId)
    return this.PostAuthenticatedAsync({
      relativePath: '/decision-upload',
      data,
      id: 'UploadQuotationDecision',
    })
  }

  delete(quotationId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/${quotationId}`,
      id: `DeleteQuotationOutbox-${quotationId}`,
    })
  }
}

export class QuotationOutboxController extends BaseController<QuotationOutboxClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new QuotationOutboxClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(quotationId: string) {
    return this.handle(() => this.api.detail(quotationId))
  }

  draft(projectId: string) {
    return this.handle(() => this.api.draft(projectId))
  }

  save(quotationId: string, request: SaveQuotationResponseCommandRequest) {
    return this.handle(() => this.api.save(quotationId, request))
  }

  send(quotationId: string, projectId: string) {
    return this.handle(() => this.api.send(quotationId, projectId))
  }

  retry(quotationId: string) {
    return this.handle(() => this.api.retry(quotationId))
  }

  changeChannel(quotationId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeChannel(quotationId, request))
  }

  download(quotationId: string) {
    return this.handle(() => this.api.download(quotationId))
  }

  uploadDecision(file: File, quotationId?: string) {
    return this.handle(() => this.api.uploadDecision(file, quotationId))
  }

  delete(quotationId: string) {
    return this.handle(() => this.api.delete(quotationId))
  }
}
